/**
 * @file Schémas de validation Yup pour les formulaires client (création + édition).
 * @module packages.common.components.customer.dialogs.schemas.customerSchemas
 */

import * as Yup from 'yup';
import {validatePhone} from 'packages/common/components/phone-input';
import {SUPPORTED_LANGUAGE_CODES, DEFAULT_LANGUAGE} from 'packages/common/locales/languageConfig';

/**
 * Schema de base pour les champs d'identite
 */
const identitySchema = {
  // Prénom optionnel : on tolère un champ vide, mais on garde la contrainte
  // de longueur minimale dès qu'une valeur est saisie (qualité des données).
  firstName: Yup.string()
      .test(
          'firstName-min',
          'Le prénom doit contenir au moins 2 caractères',
          (value) => !value || value.trim().length >= 2,
      ),
  lastName: Yup.string()
      .required('Le nom est requis')
      .min(2, 'Le nom doit contenir au moins 2 caractères'),
};

/**
 * Schema de validation email avec condition email/phone
 */
const emailSchema = Yup.string()
    .nullable()
    .email('Format email invalide')
    .test('email-or-phone', 'Email ou téléphone requis', function(value) {
      // eslint-disable-next-line no-invalid-this
      const {phone} = this.parent;
      if (!value && !phone) return false;
      return true;
    });

/**
 * Schema de validation telephone avec condition email/phone
 */
const phoneSchema = Yup.string()
    .nullable()
    .test('valid-phone', 'Numéro de téléphone invalide', (value) => {
      if (!value) return true;
      return validatePhone(value);
    })
    .test('phone-or-email', 'Email ou téléphone requis', function(value) {
      // eslint-disable-next-line no-invalid-this
      const {email} = this.parent;
      if (!value && !email) return false;
      return true;
    });

/**
 * Schema de validation pour les informations entreprise
 */
const companyInfoSchema = Yup.object().when('isCompany', {
  is: true,
  then: () => Yup.object().shape({
    companyName: Yup.string().required('Le nom de l\'entreprise est requis'),
    companyStreet: Yup.string().required('L\'adresse est requise'),
    companyNumber: Yup.string().required('Le numéro est requis'),
    companyZip: Yup.string().required('Le code postal est requis'),
    companyCity: Yup.string().required('La ville est requise'),
    companyCountry: Yup.string().required('Le pays est requis'),
    companyVAT: Yup.string().required('Le numéro de TVA est requis'),
  }),
  otherwise: () => Yup.object(),
});

/**
 * Schema de validation pour les emails additionnels
 */
const additionalEmailsSchema = Yup.array().of(
    Yup.object().shape({
      email: Yup.string()
          .email('Format email invalide')
          .required('Email requis')
          .test('not-same-as-primary', 'Cet email est déjà utilisé comme email principal', function(value) {
            // eslint-disable-next-line no-invalid-this
            const {from} = this;
            const rootValues = from?.[1]?.value;
            if (!value || !rootValues?.email) return true;
            return value.toLowerCase().trim() !== rootValues.email.toLowerCase().trim();
          }),
    }),
);

/**
 * Schema de validation pour les telephones additionnels
 */
const additionalPhonesSchema = Yup.array().of(
    Yup.object().shape({
      phone: Yup.string()
          .required('Téléphone requis')
          .test('valid-phone', 'Numéro de téléphone invalide', (value) => {
            if (!value) return true;
            return validatePhone(value);
          })
          .test('not-same-as-primary', 'Ce téléphone est déjà utilisé comme téléphone principal', function(value) {
            // eslint-disable-next-line no-invalid-this
            const {from} = this;
            const rootValues = from?.[1]?.value;
            if (!value || !rootValues?.phone) return true;
            return value !== rootValues.phone;
          }),
    }),
);

/**
 * Schema de validation pour la creation d'un client
 */
export const CustomerCreateSchema = Yup.object().shape({
  ...identitySchema,
  email: emailSchema,
  phone: phoneSchema,
  subscribeNewsletter: Yup.boolean(),
  preferredLanguage: Yup.string().oneOf(SUPPORTED_LANGUAGE_CODES, 'Langue non supportée'),
  notes: Yup.string().max(500, 'Les notes ne peuvent pas dépasser 500 caractères'),
  isCompany: Yup.boolean(),
  companyInfo: companyInfoSchema,
  additionalEmails: additionalEmailsSchema,
  additionalPhones: additionalPhonesSchema,
});

/**
 * Schema de validation pour la modification d'un client
 */
export const CustomerEditSchema = Yup.object().shape({
  ...identitySchema,
  email: emailSchema,
  phone: phoneSchema,
  isVip: Yup.boolean(),
  isBlacklisted: Yup.boolean(),
  hasNoShow: Yup.boolean(),
  excludeFromReview: Yup.boolean(),
  subscribeNewsletter: Yup.boolean(),
  preferredLanguage: Yup.string().oneOf(SUPPORTED_LANGUAGE_CODES, 'Langue non supportée'),
  notes: Yup.string().max(500, 'Les notes ne peuvent pas dépasser 500 caractères'),
  isCompany: Yup.boolean(),
  companyInfo: companyInfoSchema,
  additionalEmails: additionalEmailsSchema,
  additionalPhones: additionalPhonesSchema,
});

/**
 * Valeurs par defaut pour la creation d'un client
 */
export const customerCreateDefaults = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  subscribeNewsletter: true,
  preferredLanguage: DEFAULT_LANGUAGE,
  notes: '',
  isCompany: false,
  companyInfo: {
    companyName: '',
    companyStreet: '',
    companyNumber: '',
    companyZip: '',
    companyCity: '',
    companyCountry: 'Belgique',
    companyVAT: '',
  },
  additionalEmails: [],
  additionalPhones: [],
};

/**
 * Valeurs par defaut pour la modification d'un client
 */
export const customerEditDefaults = {
  ...customerCreateDefaults,
  subscribeNewsletter: false,
  isVip: false,
  isBlacklisted: false,
  hasNoShow: false,
  excludeFromReview: false,
};
